import Link from 'next/link';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import classnames from 'classnames';
import { Element } from 'domhandler/lib/node';
import parse, { domToReact, HTMLReactParserOptions } from 'html-react-parser';
import { SEOAccordion } from '@lifepal/lifepal-design-system';
import { BUTTON_TEXT, GTM_CLASS, GTM_CLASS_PREFIX } from './constant';
import { isV2Post } from '../utils/verifier';
import { setCTAtext } from '../utils/setCTAtext';
import useWebView from '../utils/hooks/useWebView';

import styles from '../styles/pages/SinglePost.module.scss';

// TODO: Refac the params to object based (on free time)
export function contentHandler(
  postContent: string,
  slugName: string,
  postTitle: string,
  post?: any,
  postHasFaq?: boolean,
  ctaTexts?: any
) {
  let queueCalculator = 0;
  let idsCalculator = [];
  const isWebView = useWebView();

  const categorySlug =
    post && post.categories && post.categories.nodes.length > 0 && post.categories.nodes[0].slug;
  const totalCalculator = (postContent.match(/<div id="fbuilder">/g) || []).length;

  const options: HTMLReactParserOptions = {
    replace: (domNode: Element) => {
      if (domNode.attribs) {
        if (domNode.attribs.id === 'shortcode-inarticle-banner') {
          const dataJson = domNode.attribs['data-json'];
          const dataShortcode = JSON.parse(dataJson);

          const ArticleBanner = dynamic(
            () => import('../components/Banners/ArticleBanner/ArticleBanner')
          );

          return (
            <ArticleBanner
              title={post.title}
              data={dataShortcode}
              categorySlug={categorySlug}
              type={dataShortcode.type}
            />
          );
        }

        if (domNode.attribs.id === 'shortcode-inarticle-leadform') {
          const dataJson = domNode.attribs['data-json'];
          const dataShortcode = JSON.parse(dataJson);

          const InArticleLeadForm = dynamic(
            () => import('../components/LeadForm/InArticleLeadIForm/InArticleLeadForm')
          );

          const buttonText =
            dataShortcode && dataShortcode.buttonText
              ? dataShortcode.buttonText.replace('&amp;', '&')
              : BUTTON_TEXT.default;

          return (
            <InArticleLeadForm
              category={dataShortcode.category}
              category_value={dataShortcode.category_value}
              title={dataShortcode.title.replace('&amp;', '&')}
              imageBannerDesktopURL={dataShortcode.image_banner_desktop_url}
              imageBannerMobileURL={dataShortcode.image_banner_mobile_url}
              isOpen={dataShortcode.llg_is_open}
              footerText={dataShortcode.footer_text}
              link={dataShortcode.link}
              buttonText={setCTAtext({
                leadFormType: dataShortcode.category,
                buttonText,
                ctaTextList: ctaTexts,
              })}
            />
          );
        }

        if (domNode.attribs.id === 'shortcode-accordion') {
          const dataJson = domNode.attribs['data-json'];
          const dataShortcode = JSON.parse(dataJson);

          const contentReplaced = dataShortcode.content
            .replace(new RegExp('<a ', 'g'), '<span ')
            .replace(new RegExp('</a>', 'g'), '</span>');

          const list = [
            {
              id: dataShortcode.title,
              title: dataShortcode.title,
              body: ()=> <div dangerouslySetInnerHTML={{ __html: !isWebView ? dataShortcode?.content : contentReplaced }}/>,
              h3: Boolean(dataShortcode.h3),
              hasFaqSchema: Boolean(dataShortcode.hasSchemaOrg)
            }
          ]

          return (
            <SEOAccordion
              accordionList={list}
              isMultipleOpen={true}
              hasBorder={false}
              arrowPosition="left"
            />
          );
        }

        if (domNode.attribs.id === 'shortcode-summary') {
          const dataJson = domNode.attribs['data-json'];
          const dataShortcode = dataJson && JSON.parse(dataJson);
          const Summary = dynamic(() => import('../components/Summary/Summary'));

          const childElement: any = domNode.children;
          if (childElement.length) {
            const filteredSpanElements = domNode.children.filter(
              (child) => child instanceof Element && child.name === 'a'
            );

            filteredSpanElements.forEach((element: any) => {
              if (element instanceof Element) {
                element.attribs.class = classnames(
                  GTM_CLASS.REDIRECTION_IN_ARTICLE_LINK,
                  `${GTM_CLASS_PREFIX}${categorySlug}`
                );
              }

              if (isWebView) {
                element.name = 'span';
              }
            });
          }

          return (
            <Summary
              content={domToReact(childElement)}
              variant={dataShortcode.variant}
              categorySlug={categorySlug}
            />
          );
        }

        if (domNode.attribs.id === 'shortcode-youtube' && domNode.attribs['data-videoid']) {
          const Youtube = dynamic(() => import('../components/Iframe/Youtube/Youtube'));
          const videoId = domNode.attribs['data-videoid'];

          return <Youtube videoID={videoId} videoTitle={`video__${videoId}`} />;
        }

        if (domNode.attribs.id === 'shortcode-inarticle-tanya') {
          const dataJson = domNode.attribs['data-json'];
          const dataShortcode = JSON.parse(dataJson);

          const TanyaWidget = dynamic(() => import('../components/TanyaWidget/TanyaWidget'));

          return (
            <TanyaWidget
              author={dataShortcode.author}
              title={dataShortcode.title}
              content={dataShortcode.content}
              url={dataShortcode.url}
            />
          );
        }

        if (domNode.attribs.id === 'ez-toc-container') {
          const content = domNode.children.filter((child): Element => {
            if (child instanceof Element && child.name === 'nav') {
              return child;
            }
          });

          const TOC = dynamic(() => import('../components/TOC/TOC'));
          return <TOC content={content[0]} slug={slugName} isV2={isV2Post(post)} />;
        }

        if (domNode.attribs.id === 'shortcode-faq') {
          const dataJson = domNode.attribs['data-json'];
          const dataShortcode = JSON.parse(dataJson);

          const Faq = dynamic(() => import('../components/Faq/Faq'));

          return (
            <Faq
              title={dataShortcode.title}
              content={dataShortcode.content}
              isContent={true}
              categories={post.categories}
            />
          );
        }

        if (domNode.attribs.id === 'shortcode-leads-banner') {
          const LeadsBanner = dynamic(
            () => import('../components/Banners/LeadsBanner/LeadsBanner')
          );

          return (
            <LeadsBanner
              slug={post.slug}
              utmMedium="in_article_leads_banner"
              categorySlug={categorySlug}
            />
          );
        }
      }

      if (domNode.type === 'tag' && domNode.name === 'form') {
        if (domNode.attribs.name && domNode.attribs.id.includes('cp_calculatedfieldsf_pform')) {
          const preTags: any = domNode.children.find(
            (element) => element instanceof Element && element.name === 'pre'
          );

          const inputField: any = domNode.children.find(
            (element) => element instanceof Element && element.name === 'input'
          );

          if (inputField && inputField.attribs) {
            idsCalculator.push(inputField.attribs.value);
          }

          if (preTags instanceof Element && idsCalculator.length > 0) {
            const scriptTags: any = preTags.children.find((element) => element.type === 'script');

            if (scriptTags instanceof Element && scriptTags.children.length) {
              const scriptChildren: any = scriptTags.children[0];
              const calculatorConfig = scriptChildren.data;
              queueCalculator++;

              const Calculator = dynamic(() => import('../components/Calculator/Calculator'), { ssr: false });

              return (
                <Calculator
                  id={domNode.attribs.id}
                  name={domNode.attribs.name}
                  config={calculatorConfig}
                  queue={queueCalculator}
                  total={totalCalculator}
                  ids={idsCalculator}
                  categories={post.categories}
                  slug={slugName}
                >
                  {domToReact(domNode.children)}
                </Calculator>
              );
            }
          }
        }
      }

      if (domNode.type === 'tag' && domNode.name === 'h2') {
        const faqPattern = /(tanya jawab)/gi;
        const childElement: any = domNode.children;
        if (childElement.length) {
          if (
            childElement[0].name === 'span' &&
            childElement[0].attribs &&
            childElement[0].attribs.id
          ) {
            const idTag = childElement[0].attribs.id.replace(/_/g, ' ').toLowerCase();

            if (idTag.match(faqPattern)) {
              return (
                <p id="faq-section">
                  <h2>{domToReact(domNode.children)}</h2>
                </p>
              );
            }
          }
        } else {
          return <>domToReact(domNode.children)</>;
        }
      }

      if (domNode.type === 'tag' && domNode.name === 'p') {
        const childElement: any = domNode.children;

        if (childElement.length) {
          const filteredSpanElements = domNode.children.filter(
            (child) => child instanceof Element && child.name === 'span'
          );

          filteredSpanElements.forEach((element) => {
            if (element instanceof Element) {
              element.attribs.style = '';
            }
          });

          if (isWebView) {
            domNode.children.forEach((child: any) => {
              if (child instanceof Element && child.name === 'a') {
                child.name = 'span';
              }

              if (child?.children?.length > 1) {
                const grandChildren = child.children;
                grandChildren.forEach((grandChild) => {
                  if (grandChild instanceof Element && grandChild.name === 'a') {
                    grandChild.name = 'span';
                  }
                });
              }
            });
          }

          return <p>{domToReact(domNode.children)}</p>;
        } else {
          return <></>;
        }
      }

      if (domNode.type === 'tag' && domNode.name === 'a') {
        const childElement: any = domNode.children;
        if (childElement.length) {
          if (childElement[0].type === 'text') {
            const linkTitle = domNode.attribs.title || postTitle;
            return !isWebView ? (
              <Link href={domNode.attribs.href}>
                <a
                  title={linkTitle}
                  className={classnames(
                    GTM_CLASS.REDIRECTION_IN_ARTICLE_LINK,
                    `${GTM_CLASS_PREFIX}${categorySlug}`
                  )}
                >
                  {childElement[0].data}
                </a>
              </Link>
            ) : (
              <span>{childElement[0].data}</span>
            );
          } else if (childElement[0].type === 'tag' && childElement[0].name === 'img') {
            const alt = childElement[0].attribs.alt || postTitle;

            return (
              <Link href={domNode.attribs.href}>
                <a aria-label="attribute image" title={alt}>
                  <img
                    src={childElement[0].attribs.src}
                    alt={alt}
                    width="100%"
                    height="auto"
                    loading="lazy"
                  />
                </a>
              </Link>
            );
          }
        }
      }

      if (domNode.type === 'tag' && domNode.name === 'figure') {
        domNode.attribs.class = styles['post__content-figure'];
        domNode.attribs.style = '';
        const childElement: any = domNode.children;
        if (childElement.length) {
          if (childElement[0].type === 'img') {
            const alt = childElement[0].attribs.alt || postTitle;
            return (
              <Image
                src={childElement[0].attribs.src}
                priority={false}
                width={childElement[0].attribs.width}
                height={childElement[0].attribs.height}
                sizes={childElement[0].attribs.sizes}
                alt={alt}
              />
            );
          }
        }
      }

      if (domNode.name === 'a') {
        const childElement: any = domNode.children;

        return !isWebView ? domNode : <span>{childElement[0].children[0].data}</span>;
      }
    },
  };

  return parse(postContent, options);
}

export default contentHandler;
